// [PURPOSE]: Lightweight error fallback for individual component failures
// [WHY]: Show user-friendly error when a component crashes without taking over entire screen
// [INTEGRATION]: Used with ErrorBoundary for component-specific error handling
// [CREATED]: November 12, 2025 - Phase 4.3: Error Boundaries

import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ComponentErrorFallback - Lightweight error UI for component failures
 *
 * Shows a compact error message when a specific component crashes,
 * allowing the rest of the app to continue functioning.
 *
 * @param {Object} props
 * @param {string} props.componentName - Name of the failed component
 * @param {function} props.onReset - Optional reset handler
 * @returns {React.ReactElement}
 */
const ComponentErrorFallback = ({ componentName = 'Component', onReset }) => {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="bg-red-900/20 border-2 border-red-500/50 rounded-lg p-6 m-4">
      {/* Error Icon and Title */}
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-red-500 rounded-full p-2">
          <AlertTriangle className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-red-400 font-bold text-lg">
            {componentName} Failed to Load
          </h3>
          <p className="text-gray-400 text-sm">
            This component encountered an error
          </p>
        </div>
      </div>

      {/* Error Message */}
      <div className="bg-gray-900/50 rounded p-3 mb-4">
        <p className="text-gray-300 text-sm">
          The <span className="text-red-400 font-semibold">{componentName}</span> component
          crashed while rendering. This error has been logged.
        </p>
        <p className="text-gray-400 text-xs mt-2">
          The rest of the app should continue to work normally.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        {onReset && (
          <button
            onClick={onReset}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        )}
        <button
          onClick={handleReload}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Reload Page
        </button>
      </div>

      {/* Technical Note */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <p className="text-gray-500 text-xs">
          💡 <span className="font-semibold">Developer Note:</span> Check the browser console for error details.
          The error has also been logged to the activity feed if available.
        </p>
      </div>
    </div>
  );
};

export default ComponentErrorFallback;
